import React, { useState } from 'react';
import { useToast } from '@crisellevaldez/speculo-ui';
import { RemiIdxSetupData, MLSSetupData, IDXSiteSetupData } from '../schemas/onboarding.schema';
import { MLSSetupStep } from './MLSSetupStep';
import { IDXSiteSetupStep } from './IDXSiteSetupStep';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useUpdateOrganizationSettings } from '../../../../hooks/organizations/useUpdateOrganizationSettings';
import { useQueryClient } from '@tanstack/react-query';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { useCreateWebsite } from '../../../../hooks/client/tenant/useCreateWebsite';
import { useOrganizationLogo } from '../../../../hooks/organizations/useOrganizationLogo';
import { useOrganizationFavicon } from '../../../../hooks/organizations/useOrganizationFavicon';
import { clearOrganizationCache } from '../../../../utils/cacheUtils';
import { toStoredSiteData } from '../utils/remiIdxSite';

interface Stage4IDXSetupWrapperProps {
  onNext: (data: RemiIdxSetupData) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialData?: Partial<RemiIdxSetupData> | any;
  isLoading?: boolean;
  userEmail?: string;
  userName?: string;
}

export const Stage4IDXSetupWrapper: React.FC<Stage4IDXSetupWrapperProps> = ({
  onNext,
  initialData,
  isLoading = false,
  userEmail = '',
  userName = '',
}) => {
  const { organization, fetchOrganization } = useOrganizationStore();
  const { updateSettings } = useUpdateOrganizationSettings();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Hooks for creating IDX site immediately
  const createWebsite = useCreateWebsite();
  const { uploadLogo } = useOrganizationLogo();
  const { uploadFavicon } = useOrganizationFavicon();

  // State for 2-step flow: site -> mls (IDX site first, then MLS submission)
  const [currentStep, setCurrentStep] = useState<'site' | 'mls'>(() => {
    // Check if IDX site data was saved - if so, go to MLS step
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((organization as any)?.onboardingData?.idx_site_completed) {
      return 'mls';
    }
    return 'site';
  });

  const [savedSiteData, setSavedSiteData] = useState<IDXSiteSetupData | null>(null);
  const [isSavingSite, setIsSavingSite] = useState(false);
  const { sendWebhook } = useOnboardingWebhook();

  // Step 1: IDX Site Setup - create IDX site immediately and move to MLS
  const handleSiteSubmit = async (data: IDXSiteSetupData) => {
    if (!organization?.id) return;

    setIsSavingSite(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const siteData = data as any;
      const isIdxSkipped = siteData?.idx_skipped === true;

      let logoUrl = organization.logoUrl || '';
      let faviconUrl = organization.faviconUrl || '';

      // Build IDX site data object for Firestore (without File objects)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { logo_file, favicon_file, ...dataWithoutFiles } = siteData;

      if (!isIdxSkipped) {
        // NON-SKIPPED FLOW: Create IDX site immediately using createWebsite hook
        // This handles: domain validation, domain check, domain registration, file uploads, tenant creation

        // Calculate full domain
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const websiteDomain = (organization as any)?.onboardingData?.website?.domain_name || '';
        const fullDomain = siteData.idx_subdomain
          ? siteData.has_website === 'no' && siteData.new_domain
            ? `${siteData.idx_subdomain}.${siteData.new_domain}`
            : `${siteData.idx_subdomain}.${websiteDomain}`
          : '';

        // Prepare compliance links array from dynamic links
        const complianceLinksArray = siteData.compliance_links || [];

        if (fullDomain) {
          // Use logo_url from form data (already contains existing URL from Stage 2 or new data URL)
          const existingLogoForTenant = siteData.logo_url || organization.logoUrl || '';
          const existingFaviconForTenant = organization.faviconUrl || '';

          const result = await createWebsite.mutateAsync({
            organizationId: organization.id,
            organizationName: organization.org_name || 'IDX Site',
            domain: fullDomain,
            logoFile: siteData.logo_file || null,
            faviconFile: siteData.favicon_file || null,
            existingLogoUrl: existingLogoForTenant,
            existingFaviconUrl: existingFaviconForTenant,
            logoWidth: siteData.logo_width || 200,
            logoHeight: siteData.logo_height || 100,
            logoPlacement: siteData.logo_placement || 'header',
            colors: {
              primary: siteData.primary_color || '#000000',
              secondary: siteData.secondary_color || '#000000',
            },
            complianceLinks: complianceLinksArray,
          });

          // Update logo/favicon URLs from hook result
          logoUrl = result.logoUrl;
          faviconUrl = result.faviconUrl;
        }
      } else {
        // SKIPPED FLOW: Only upload files (no domain registration or tenant creation)
        if (siteData?.logo_file) {
          try {
            logoUrl = await uploadLogo(siteData.logo_file, organization.id, logoUrl);
          } catch (uploadError) {
            console.error('Error uploading logo:', uploadError);
          }
        }

        if (siteData?.favicon_file) {
          try {
            faviconUrl = await uploadFavicon(siteData.favicon_file, organization.id, faviconUrl);
          } catch (uploadError) {
            console.error('Error uploading favicon:', uploadError);
          }
        }
      }

      // Save IDX site data to organization (with updated logo/favicon URLs)
      await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...((useOrganizationStore.getState().organization as any)?.onboardingData ?? {}),
            idx_site: {
              ...toStoredSiteData(dataWithoutFiles),
              logo_url: logoUrl,
              favicon_url: faviconUrl,
              status: isIdxSkipped ? 'skipped' : 'pending_dns',
            },
            idx_site_completed: true,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });

      // Clear caches and refetch organization data
      await clearOrganizationCache(queryClient, fetchOrganization);

      // Save data locally (for combining with MLS data later)
      setSavedSiteData({
        ...data,
        logo_url: logoUrl,
        favicon_url: faviconUrl,
      } as IDXSiteSetupData);

      // Send webhook for IDX site step
      const webhookLabel = isIdxSkipped
        ? 'Stage 4 - Step 2: IDX Site Setup (Skipped)'
        : 'Stage 4 - Step 2: IDX Site Setup Complete';
      sendWebhook(webhookLabel, {
        ...dataWithoutFiles,
        logo_url: logoUrl,
        favicon_url: faviconUrl,
      });

      showToast({
        message: isIdxSkipped
          ? "IDX site setup skipped. Now let's set up your MLS licensing."
          : "IDX site created successfully! Now let's set up your MLS licensing.",
        variant: 'success',
      });

      setCurrentStep('mls');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving IDX site setup:', error);

      // Extract error message for user-friendly display
      let errorMessage = 'Failed to save IDX site information. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('already registered to another organization')) {
          errorMessage = error.message;
        } else if (error.message.includes('Failed to register domain')) {
          errorMessage =
            'Failed to register domain. Please check your domain settings and try again.';
        } else if (error.message.includes('Failed to create tenant')) {
          errorMessage = 'Failed to create your IDX site. Please try again or contact support.';
        } else if (error.message.includes('Invalid domain')) {
          errorMessage = 'Invalid domain format. Please check your domain and subdomain settings.';
        } else if (error.message.includes('Failed to upload logo')) {
          errorMessage = 'Failed to upload logo. Please try a different image file.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      showToast({
        message: errorMessage,
      });
    } finally {
      setIsSavingSite(false);
    }
  };

  // Step 2: MLS Submission - final step, combine data and complete stage
  const handleMLSSubmit = async (data: MLSSetupData) => {
    // Combine site data with MLS data
    // Site data already has logo_url and favicon_url from the IDX creation step
    const combinedData: RemiIdxSetupData = {
      site_data:
        savedSiteData ||
        (initialData?.site_data ? initialData.site_data : null) ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (organization as any)?.onboardingData?.idx_site ||
        {},
      mls_data: data,
    };

    // Send webhook for MLS step completion
    sendWebhook('Stage 4 - Step 1: MLS Licensing Agreement', data);

    // Pass to parent (no need to create website again - already done in step 1)
    onNext(combinedData);
  };

  // STEP 2: MLS Setup (after IDX site)
  if (currentStep === 'mls') {
    return (
      <MLSSetupStep
        onNext={handleMLSSubmit}
        initialData={
          initialData?.mls_data ||
          (initialData?.mls_entries ? { mls_entries: initialData.mls_entries } : undefined)
        }
        isLoading={isLoading}
        userEmail={userEmail}
        userName={userName}
        idxSubdomain={
          savedSiteData?.idx_subdomain ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (organization as any)?.onboardingData?.idx_site?.idx_subdomain ||
          ''
        }
      />
    );
  }

  // STEP 1: IDX Site Setup (first step)
  return (
    <IDXSiteSetupStep
      onNext={handleSiteSubmit}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      initialData={initialData?.site_data || (organization as any)?.onboardingData?.idx_site}
      isLoading={isSavingSite}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      websiteData={(organization as any)?.onboardingData?.website}
      orgName={organization?.org_name || ''}
      brokerageName=""
    />
  );
};
